import { Plus, Trash2 } from "lucide-react";
import { Dispatch, useMemo } from "react";

import Field from "@/components/field";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { Textarea } from "@buildpy/ui/textarea";
import { EditorAction } from "@buildpy/core/modules/quote/editor/types";
import { lockedNow, NEVER_LOCKED, unlessLocked } from "@/modules/quote/ui/editor/locked-guard";

interface Props {
  conditions: string[];
  /** conditions 와 같은 길이의 안정 key. 줄을 지워도 포커스가 안 튀게. */
  conditionKeys: string[];
  extraNotice: string;
  dispatch: Dispatch<EditorAction>;
  // 잠긴 견적 — 칸·단추를 끄고, 재렌더 전에 들어온 입력도 isLocked 로 버린다.
  disabled?: boolean;
  isLocked?: () => boolean;
}

/** 갑지 — 고객이 표보다 먼저 읽는 줄들이다. 견적조건은 한 줄이 한 항목. */
const CoverEditor = ({
  conditions,
  conditionKeys,
  extraNotice,
  dispatch: rawDispatch,
  disabled = false,
  isLocked = NEVER_LOCKED,
}: Props) => {
  const dispatch = useMemo(() => unlessLocked(lockedNow(disabled, isLocked), rawDispatch), [disabled, isLocked, rawDispatch]);
  return (
    <SectionCard padding="sm">
      <h2 className="pb-2 text-sm font-medium">갑지</h2>

      <div className="flex flex-col gap-y-1">
        <span className="text-xs text-muted-foreground">견적 조건</span>
        {conditions.length === 0 && (
          <p className="py-1 text-sm text-muted-foreground">
            조건 줄이 없습니다.
          </p>
        )}
        {conditions.map((line, index) => (
          <div
            key={conditionKeys[index] ?? `condition-${index}`}
            className="flex items-center gap-x-1">
            <span className="w-5 text-right text-xs text-muted-foreground">
              {index + 1}
            </span>
            <div className="flex-1">
              <Input
                aria-label={`견적 조건 ${index + 1}`}
                disabled={disabled}
                inputSize="sm"
                name={`condition-${index}`}
                value={line}
                onChange={(e) =>
                  dispatch({
                    type: "SET_CONDITION",
                    index,
                    value: e.target.value,
                  })
                }
              />
            </div>
            <Button
              aria-label={`견적 조건 ${index + 1} 삭제`}
              disabled={disabled}
              size="icon-xs"
              type="button"
              variant="ghost-destructive"
              onClick={() => dispatch({ type: "REMOVE_CONDITION", index })}>
              <Trash2 className="size-4" />
            </Button>
          </div>
        ))}

        <div>
          <Button
            className="mt-1"
            disabled={disabled}
            size="sm"
            variant="outline"
            onClick={() => dispatch({ type: "ADD_CONDITION" })}>
            <Plus className="size-4" /> 줄 추가
          </Button>
        </div>
      </div>

      <Field className="mt-3" label="별도 부담금 안내">
        <Textarea
          aria-label="별도 부담금 안내"
          className="min-h-20 text-sm"
          disabled={disabled}
          placeholder="견적 외 별도로 부담하시는 항목을 적습니다."
          value={extraNotice}
          onChange={(e) =>
            dispatch({ type: "SET_EXTRA_NOTICE", value: e.target.value })
          }
        />
      </Field>
    </SectionCard>
  );
};

export default CoverEditor;
